
import { supabase } from "@/integrations/supabase/client";

export interface SubstackPost {
  id: string;
  title: string;
  description?: string;
  url: string;
  published_at: string;
  author?: string;
  thumbnail_url?: string;
  tags: string[];
  content_excerpt?: string;
  created_at: string;
}

export const fetchSubstackPosts = async (limit = 10) => {
  console.log('Fetching Substack posts...');
  
  try {
    // Force refresh from RSS feed every time to ensure we have the latest data
    console.log('Invoking substack RSS edge function...');
    const { data: response, error: functionError } = await supabase.functions.invoke('fetch-substack-rss');
    
    if (functionError) {
      console.error('Edge function error:', functionError);
      
      // Fallback to database if edge function fails
      console.log('Falling back to cached posts...');
      const { data: cachedPosts, error: cacheError } = await supabase
        .from('substack_posts')
        .select('*')
        .order('published_at', { ascending: false })
        .limit(limit);
        
      if (cacheError) {
        console.error('Error fetching cached posts:', cacheError);
        throw cacheError;
      }
      
      console.log(`Returning ${cachedPosts?.length || 0} cached posts`);
      console.log('Post titles:', cachedPosts?.map(p => p.title));
      return cachedPosts as SubstackPost[] || [];
    }

    if (response && response.posts) {
      console.log(`Received ${response.posts.length} posts from edge function`);
      console.log('Post titles:', response.posts.map((p: SubstackPost) => p.title));
      return response.posts.slice(0, limit) as SubstackPost[];
    }

    // Last resort fallback - return empty array
    console.log('No posts returned from edge function, returning empty array');
    return [];
    
  } catch (error) {
    console.error('Error in fetchSubstackPosts:', error);
    
    // Final fallback - return empty array
    return [];
  }
};

// Seed some sample Substack posts for demonstration
export const seedSamplePosts = async () => {
  const samplePosts = [
    {
      title: "The Future of AI Prompt Engineering: 10 Trends to Watch",
      description: "Explore the latest trends shaping the future of prompt engineering and how they'll impact your AI workflows.",
      url: "https://getprompts.substack.com/p/future-ai-prompt-engineering",
      published_at: new Date().toISOString(),
      author: "GetPrompts Team",
      tags: ["prompt engineering", "ai tools", "trends"],
      content_excerpt: "As AI continues to evolve, prompt engineering remains at the forefront of maximizing AI capabilities. Here are the key trends that will shape the field in the coming year..."
    },
    {
      title: "Mastering ChatGPT for Product Management",
      description: "A comprehensive guide to using ChatGPT effectively for product management tasks and workflows.",
      url: "https://getprompts.substack.com/p/chatgpt-product-management",
      published_at: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
      author: "GetPrompts Team",
      tags: ["chatgpt", "product management", "productivity"],
      content_excerpt: "Product managers can leverage ChatGPT to streamline their workflows, from user research to feature planning. This guide covers practical techniques..."
    }
  ];

  for (const post of samplePosts) {
    await supabase
      .from('substack_posts')
      .upsert(post, { onConflict: 'url' });
  }
};
